import { IdsAdapterService, LanguagesService, ProjectService } from '@alpha-core/platform-ui';
import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { GroupService } from '../../../services/group.service';
import { userService } from '../../../services/user-service';
import { ADMIN_GROUP } from '../../../shared/alpha-constants';
import { PrimeNGModules } from '../../../shared/alpha-shared.module';
import { DialogComponent } from '../../dialog/dialog.component';
import { CheckboxListComponent } from '../checkbox-list/checkbox-list.component';
import { EditableListComponent } from '../editable-list/editable-list.component';
import { Group } from '../user.types';
export interface UserMetadata {
    userName: string;
    bandwidth: number;
    remainingBandwidth: number;
    dailyLimit: number;
    remainingDailyLimit: number;
    isActive: boolean;
    enableTaskDistribution: boolean;
    [key: string]: any;
}

@Component({
    selector: 'app-create-user',
    standalone: true,
    providers: [DialogService],
    templateUrl: './create-user.component.html',
    imports: [
        PrimeNGModules,
        ReactiveFormsModule,
        FormsModule,
        CheckboxListComponent,
        EditableListComponent,
        TranslatePipe,
    ],
})
export class CreateUserComponent {
    private groupService = inject(GroupService);
    private router = inject(Router);
    private formBuilder = inject(FormBuilder);
    private dialogService = inject(DialogService);
    private userService = inject(userService);
    private messageService = inject(MessageService);
    private projectService = inject(ProjectService);
    private languageService = inject(LanguagesService);
    private idsAdapterService = inject(IdsAdapterService);

    managerMenus = [
        {
            title: 'Add reporting manager to user',
            icon: 'reporting-manager.svg',
            activeIcon: 'reporting-manager-active-icon.svg',
        },
        {
            title: 'Add subordinates to user',
            icon: 'subbordinates.svg',
            activeIcon: 'subbordinates-active-icon.svg',
        },
    ];

    optionalMenu = [
        {
            title: 'default-cm-addUsersToGroups',
            icon: 'group-user.svg',
            activeIcon: 'group-user-active-icon.svg',
        },
        {
            title: 'default-cm-addSkillsToUser',
            icon: 'skills.svg',
            activeIcon: 'skills-active-icon.svg',
        },
        {
            title: 'default-cm-addReportees',
            icon: 'skills.svg',
            activeIcon: 'skills-active-icon.svg',
        },
        // {
        //   title: 'Add locations to user',
        //   icon: 'location.svg',
        //   activeIcon: 'location-active-icon.svg'
        // },
    ];
    activeMenuIndex = 0;
    categories: any[] = [
        // {
        //   categoryMenu: 'Manager & Subordinates',
        //   items: this.managerMenus
        // },
        {
            categoryMenu: 'default-cm-optional',
            items: this.optionalMenu,
        },
    ];
    selectTabMenu: {
        label: string;
        formName: string;
        filterData: any[];
    } = {
        label: '',
        formName: '',
        filterData: [],
    };

    // component headers
    headers = [
        // 'Select reporting manager',
        // 'Select subordinates',
        'default-cm-selectGroupsToAdd',
        'default-cm-selectSkillsToAdd',
        'default-cm-selectReporteedToAdd',
        // 'Select location to add'
    ];

    reportees: any;
    groups: any[] = [];
    activePageMenu: any = 0;
    sideBar = false;
    searchKey = '';
    filterData: any[] = [];
    updateUser = false;
    private groupPage = 0;
    private readonly groupPageSize = 10;
    allGroupsLoaded = false;
    loadMoreGroupsError = false;
    currentUser?: UserMetadata;
    @ViewChild('userNameInput') userNameInput!: ElementRef;

    createUser: FormGroup<any> = new FormGroup(
        {
            // firstName: new FormControl('', [Validators.required]),
            // lastName: new FormControl('', [Validators.required]),
            userName: new FormControl('', [Validators.required, Validators.maxLength(255)]),
            bandwidth: new FormControl('', [Validators.required]),
            remainingBandwidth: new FormControl(''),
            dailyLimit: new FormControl('', [Validators.required]),
            remainingDailyLimit: new FormControl(''),
            isActive: new FormControl(false),
            enableTaskDistribution: new FormControl(false),
            // reference: new FormControl(),
            groups: this.formBuilder.array([]),
            skills: this.formBuilder.array([]),
            reportees: this.formBuilder.array([]),
        },
        this.bandwidthLessThanDailyLimitValidator,
    );

    allReportees: any[] = [];

    async ngOnInit() {
        const userData: any = JSON.parse(localStorage.getItem('updateUser') || '{}');
        const allUsers = await lastValueFrom(this.userService.fetchUserWithReportees(userData.user_id));
        this.allReportees = allUsers?.allUsersWithReporteeFlag || [];
        if (userData.user_id) {
            try {
                const ancestorsRes = await lastValueFrom(this.userService.getAncestors(userData.user_id));
                const ancestorUsernames = new Set(ancestorsRes?.ancestors || []);
                this.allReportees = this.allReportees.map((u: any) => ({
                    ...u,
                    disabled: ancestorUsernames.has(u.username),
                }));
            } catch (error: any) {
                const errorMsg = error?.error?.message || error?.message || 'Unable to load hierarchy';
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Hierarchy Issue',
                    detail: errorMsg,
                    life: 8000,
                });
            }
        }
        this.selectedReportees(this.allReportees);
        if (this.router.url.includes('update-user')) {
            this.updateUser = true;
            const fetchedUserData = await lastValueFrom(this.userService.fetchUser(userData.user_id));
            const user = fetchedUserData.umas[0];
            this.currentUser = {
                userName: user?.user_id || '',
                bandwidth: user?.metadata?.bandwidth || 0,
                remainingBandwidth: user?.metadata?.remainingBandwidth || 0,
                dailyLimit: user?.metadata?.dailyLimit || 0,
                remainingDailyLimit: user?.metadata?.remainingDailyLimit || 0,
                isActive: user?.metadata?.isActive || false,
                enableTaskDistribution: user?.metadata?.enableTaskDistribution || false,
            };
            this.addDataToskills(user?.metadata?.skills || []);
            this.selectedGroups(user?.groups || []);
            this.createUser.patchValue(this.currentUser);
        }

        // by default it will load first side bar
        this.sideBar = true;
        this.activePageMenu = {
            activeMenuCategory: 0,
            activeCategoryItem: 0,
        };
        await this.fetchGroups();
        this.selectTab(this.activeMenuIndex);
        this.updateRemainingValue('bandwidth', 'remainingBandwidth');
        this.updateRemainingValue('dailyLimit', 'remainingDailyLimit');
    }

    private updateRemainingValue(controlName: string, remainingControlName: string): void {
        this.createUser.get(controlName)?.valueChanges.subscribe((newValue) => {
            if (!this.currentUser) return;

            const currentValue = this.currentUser[controlName] ?? 0;
            const currentRemainingValue = this.currentUser[remainingControlName] ?? 0;

            const newRemainingValue = currentRemainingValue + (newValue - currentValue);
            this.createUser.get(remainingControlName)?.setValue(newRemainingValue, { emitEvent: false });
        });
    }

    bandwidthLessThanDailyLimitValidator(control: AbstractControl): { [key: string]: boolean } | null {
        const bandwidth = control.get('bandwidth')?.value;
        const dailyLimit = control.get('dailyLimit')?.value;

        //! If the bandwidth is greater than the daily limit, this will return true and throw a message.
        if (bandwidth !== '' && dailyLimit !== '' && bandwidth > dailyLimit) {
            return { bandwidthGreaterThanDailyLimit: true };
        }

        return null; // No error
    }

    addDataToskills(skills: any) {
        if (Array.isArray(skills)) {
            skills.forEach((skill: any) => {
                let s = skill;
                if (typeof s === 'string') s = { skillName: s, rating: 5 };
                this.skillsArray.push(this.formBuilder.control(s));
            });
        }
    }

    ClearAll() {
        this.createUser.reset({
            isActive: false,
            enableTaskDistribution: false,
        });

        this.groupArray.clear();
        this.skillsArray.clear();
        this.reporteesArray.clear();

        this.groups.forEach((group: any) => {
            group.checked = false;
        });
        this.filterData = [...this.groups];

        const resetReportees = this.allReportees.map((u) => ({ ...u, reportee: false }));
        this.selectedReportees(resetReportees);

        this.sideBar = false;
        this.activePageMenu = {};
        this.userNameInput.nativeElement.focus();
    }

    setMenu(page: any, parentIndex: number, pageIndex: number) {
        this.sideBar = true;
        this.activePageMenu = {
            activeMenuCategory: parentIndex,
            activeCategoryItem: pageIndex,
        };
        if (parentIndex == 1) {
            pageIndex = pageIndex + 2;
        }
        this.activeMenuIndex = pageIndex;
        this.selectTab(this.activeMenuIndex);
    }

    selectTab(activeIndex: number) {
        switch (activeIndex) {
            case 0:
                this.selectTabMenu = {
                    label: 'group_name',
                    formName: 'groups',
                    filterData: [...this.filterData],
                };
                break;
            case 1:
                this.selectTabMenu = {
                    label: 'skill_name',
                    formName: 'skills',
                    filterData: [...this.skillsArray.value],
                };
                break;
            case 2:
                this.selectTabMenu = {
                    label: 'reportees',
                    formName: 'reportees',
                    filterData: [...this.reporteesArray.value],
                };
                break;
            default:
                this.selectTabMenu = {
                    label: '',
                    formName: '',
                    filterData: [],
                };
                break;
        }
    }

    async fetchGroups() {
        const res = await this.idsAdapterService.fetchAllGroups({
            pageSize: this.groupPageSize,
            pageNumber: this.groupPage,
            search: this.searchKey,
        });
        const newGroups = res.groups || [];
        const selectedGroupNames = new Set(this.groupArray.value.map((g: any) => g.group_name));
        newGroups.forEach((g: any) => {
            if (selectedGroupNames.has(g.group_name)) {
                g.checked = true;
            }
        });
        if (this.searchKey) {
            this.groups = newGroups;
        } else {
            if (this.groupPage === 0) {
                this.groups = newGroups;
            } else {
                this.groups = [...this.groups, ...newGroups];
            }
        }
        this.filterData = this.groups;
        if (res.totalCount <= this.groups.length) {
            this.allGroupsLoaded = true;
        }
        return this.groups;
    }

    async loadMoreGroups() {
        this.groupPage++;
        this.loadMoreGroupsError = false;
        try {
            await this.fetchGroups();
            if (this.activeMenuIndex === 0) {
                this.selectTab(0);
            }
        } catch {
            this.groupPage--;
            this.loadMoreGroupsError = true;
        }
    }

    close() {
        this.sideBar = false;
    }

    cancel() {
        this.navigateTo('home/users/all-users');
    }
    navigateTo(path: string) {
        const project = this.projectService.fetchCurrentProject();
        this.router.navigate([path], {
            queryParams: {
                projectId: project?.projectId,
            },
        });
    }

    async submit() {
        if (!this.createUser.valid) return;

        const body = {
            ...this.createUser.value,
            remainingBandwidth: this.currentUser?.['remainingBandwidth'],
            remainingDailyLimit: this.currentUser?.['remainingDailyLimit'],
        };
        body.reportees = body.reportees?.filter((r: any) => r.reportee) || [];

        if (this.updateUser) {
            // update existing data
            const isAdmin = body.groups.some((g: Group) => g.group_name === ADMIN_GROUP);
            const user = this.userService.userInfo;

            if (!isAdmin && body.userName === user?.response.username) {
                //if admin group have <= 1 member, restrict update
                const adminUsers = await lastValueFrom(this.groupService.getUsersByGroup(ADMIN_GROUP));
                if (adminUsers.length <= 1) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Failed',
                        detail: `At least one user should present in ${ADMIN_GROUP}`,
                    });
                    return;
                }

                this.dialogService
                    .open(DialogComponent, {
                        data: {
                            dialogType: 'confirm',
                            message: 'default-cm-updateUserConfirmationMessage',
                        },
                        header: this.languageService.getTranslation('default-cm-confirm') || 'Confirm',
                        width: '35rem',
                    })
                    .onClose.subscribe((data: any) => {
                        if (data) {
                            this.updateUserData(body);
                            this.userService.logOut();
                        }
                    });
                return;
            }

            this.updateUserData(body);
        } else {
            // create new user
            this.userService.createUser(body).then(
                () => {
                    this.messageService.add({ severity: 'success', summary: 'Saved!', detail: 'Created new user' });
                    this.navigateToUsersTable();
                },
                (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: error?.error?.stack?.message || 'User creation failed',
                        detail: error?.error?.error?.message || 'error',
                    });
                },
            );
        }
    }

    updateUserData(data: any) {
        this.userService.updateUser(data).then(
            () => {
                this.messageService.add({ severity: 'success', summary: 'Saved!', detail: 'Updated user details' });
                this.navigateToUsersTable();
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'User update failed',
                    detail: error?.error?.message || 'User update failed',
                });
            },
        );
    }

    navigateToUsersTable() {
        this.navigateTo('home/users/all-users');
    }

    toggleSelection(event: { item: any; checked: boolean }) {
        const formArrayName = this.selectTabMenu.formName;
        const formArray = this.createUser.get(formArrayName) as FormArray;
        const matchKey = formArrayName === 'groups' ? 'group_name' : null;
        if (event.checked) {
            const alreadyExists = matchKey
                ? formArray.value.some((v: any) => v[matchKey] === event.item[matchKey])
                : formArray.value.includes(event.item);
            if (!alreadyExists) {
                formArray.push(this.formBuilder.control(event.item));
            }
        } else {
            const idx = matchKey
                ? formArray.value.findIndex((v: any) => v[matchKey] === event.item[matchKey])
                : formArray.value.indexOf(event.item);
            if (idx !== -1) {
                formArray.removeAt(idx);
            }
        }
    }

    onSelectAll(checked: boolean) {
        const formArrayName = this.selectTabMenu.formName;
        const formArray = this.createUser.get(formArrayName) as FormArray;
        const matchKey = formArrayName === 'groups' ? 'group_name' : null;
        (this.selectTabMenu.filterData || []).forEach((item: any) => {
            item.checked = checked;
            if (checked) {
                const alreadyExists = matchKey
                    ? formArray.value.some((v: any) => v[matchKey] === item[matchKey])
                    : formArray.value.includes(item);
                if (!alreadyExists) {
                    formArray.push(this.formBuilder.control(item));
                }
            } else {
                const idx = matchKey
                    ? formArray.value.findIndex((v: any) => v[matchKey] === item[matchKey])
                    : formArray.value.indexOf(item);
                if (idx !== -1) {
                    formArray.removeAt(idx);
                }
            }
        });
    }

    get skillsArray() {
        return this.createUser.get('skills') as FormArray;
    }

    get groupArray() {
        return this.createUser.get('groups') as FormArray;
    }
    get reporteesArray() {
        return this.createUser.get('reportees') as FormArray;
    }

    get userName(): FormControl {
        return this.createUser.get('userName') as FormControl;
    }

    addNewSkill() {
        this.dialogService
            .open(DialogComponent, {
                data: {
                    dialogType: 'input-field',
                },
                header: this.languageService.getTranslation('default-cm-addSkill') || 'Add skill',
                width: '35rem',
            })
            .onClose.subscribe((data: any) => {
                if (!data) return;
                data.skillName = data.skillName.trim();
                const valueLowerCase = data.skillName.toLowerCase();
                const duplicate = this.skillsArray.value.some(
                    (skill: Record<string, any>) => skill['skillName'].toLowerCase() == valueLowerCase,
                );
                if (duplicate) {
                    this.messageService.add({
                        severity: 'error',
                        detail: `${data.skillName} already exists`,
                        summary: 'Error',
                    });
                    return;
                }
                this.skillsArray.push(this.formBuilder.control(data));
                this.selectTab(this.activeMenuIndex);
            });
    }

    filter(searchValue: string) {
        const regex = new RegExp(searchValue.toLowerCase().trim(), 'i');
        const label = this.selectTabMenu.label;
        if (label === 'group_name') {
            this.groupPage = 0;
            this.allGroupsLoaded = false;
            this.searchKey = searchValue;
            this.fetchGroups().then((groups) => {
                this.selectTabMenu.filterData = groups;
            });
        } else if (label === 'skill_name') {
            this.selectTabMenu.filterData = this.skillsArray.value.filter((skills: any) =>
                regex.test(skills.skillName.toLowerCase()),
            );
        } else if (label === 'reportees') {
            this.selectTabMenu.filterData = this.reporteesArray.value.filter((r: any) =>
                regex.test(r.username.toLowerCase()),
            );
        }
    }

    // selected groups
    selectedGroups(selectedGroups: any) {
        selectedGroups.forEach((obj1: any) => {
            this.groupArray.push(this.formBuilder.control(obj1));
        });
    }

    private selectedReportees(users: any[]) {
        users.forEach((u: any) => this.reporteesArray.push(this.formBuilder.control(u)));
    }

    editItem(item: any) {
        const skill = structuredClone(item);
        this.dialogService
            .open(DialogComponent, {
                data: {
                    dialogType: 'input-field',
                    skill,
                },
                header: this.languageService.getTranslation('default-cm-editSkill') || 'Edit skill',
                width: '35rem',
            })
            .onClose.subscribe((data: any) => {
                if (!data) return;
                data.skillName = data.skillName.trim();
                const index = this.skillsArray.controls.findIndex((ctrl) => ctrl.value.skillName === item.skillName);
                if (index === -1) return;

                const valueLowerCase = data.skillName.toLowerCase();
                const duplicate = this.skillsArray.controls.some(
                    (ctrl, i) => i !== index && ctrl.value.skillName?.toLowerCase() === valueLowerCase,
                );
                if (duplicate) {
                    this.messageService.add({
                        severity: 'error',
                        detail: `${data.skillName} already exists`,
                        summary: 'Error',
                    });
                    return;
                }
                this.skillsArray.at(index).setValue(data);
                this.selectTab(this.activeMenuIndex);
            });
    }

    deleteItem(item: any) {
        this.dialogService
            .open(DialogComponent, {
                data: {
                    dialogType: 'confirm',
                },
                header: this.languageService.getTranslation('default-cm-confirm') || 'Confirm',
                width: '35rem',
            })
            .onClose.subscribe((data: any) => {
                if (data) {
                    const idx = this.skillsArray.controls.findIndex((ctrl) => ctrl.value.skillName === item.skillName);
                    if (idx !== -1) {
                        this.skillsArray.removeAt(idx);
                        this.selectTab(this.activeMenuIndex);
                        this.messageService.add({
                            severity: 'warn',
                            detail: 'Please save your changes',
                            summary: 'Info',
                        });
                    }
                }
            });
    }

    reporteeDisabledTooltip = (item: any): string => {
        return item.username + ' (manager in hierarchy \u2014 cannot add as reportee)';
    };
}
